import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Upload,
  Divider,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined, DollarOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { warehouseGoodsCatalogService } from '../services/warehouseGoodsCatalogService';
import { warehouseService } from '../services/warehouseService';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import { WarehouseEntry } from '../types/warehouse';
import {
  isMassBaseUnit,
  parseGoodsCatalogExcel,
  parseGoodsCatalogPaste,
  parsedRowsToDraftItems,
  validateWarehouseGoodsNamesUnique,
} from '../utils/warehouseGoodsCatalogImportUtils';
import { expenseCatalogService, expenseEntryService } from '../services/expenseCatalogService';
import type { ExpenseCatalogItem, ExpenseEntry } from '../types/expenseCatalog';
import { buildWarehouseStockRows } from '../utils/warehouseStock';

const { Text } = Typography;

interface WarehouseFormValues {
  month: Dayjs;
  coffeeTypeId: string;
  quantity: number;
  rawQuantity?: number;
  unitPrice: number;
  notes?: string;
}

interface MonthSummaryRow {
  key: string;
  coffeeTypeName: string;
  unit: string;
  totalQuantity: number;
  totalAmount: number;
  entryCount: number;
}

interface StockRow {
  key: string;
  coffeeTypeId: string;
  coffeeTypeName: string;
  unit: string;
  totalImport: number;
  totalExportStore: number;
  totalExportPartner: number;
  totalExport: number;
  stock: number;
}

interface StoreExportSummaryRow {
  key: string;
  storeLocationId?: string;
  storeName: string;
  totalQuantity: number;
  entryCount: number;
}

const formatVi = (value: number, digits = 0) =>
  new Intl.NumberFormat('vi-VN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(value) ? value : 0);

const normalizeText = (value?: string) =>
  String(value || '')
    .trim()
    .toLowerCase();

export interface KhoTongSectionProps {
  /** Mỗi lần tăng (vd. khi chọn lại tab Kho tổng), tải lại danh mục + phiếu kho. */
  reloadToken?: number;
}

const KhoTongSection: React.FC<KhoTongSectionProps> = ({ reloadToken = 0 }) => {
  const [goodsForm] = Form.useForm<WarehouseGoodsItem>();
  const [warehouseForm] = Form.useForm<WarehouseFormValues>();
  const [expenseCatalogForm] = Form.useForm<ExpenseCatalogItem>();
  const [expenseEntryForm] = Form.useForm<{ date: Dayjs; expenseCatalogId: string; unitPrice: number; notes?: string }>();

  const [goods, setGoods] = useState<WarehouseGoodsItem[]>([]);
  const [entries, setEntries] = useState<WarehouseEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Expense catalog state
  const [expenseCatalog, setExpenseCatalog] = useState<ExpenseCatalogItem[]>([]);
  const [expenseEntries, setExpenseEntries] = useState<ExpenseEntry[]>([]);
  const [expenseCatalogModalOpen, setExpenseCatalogModalOpen] = useState(false);
  const [editingExpenseCatalog, setEditingExpenseCatalog] = useState<ExpenseCatalogItem | null>(null);
  const [expenseEntryModalOpen, setExpenseEntryModalOpen] = useState(false);
  const [editingExpenseEntry, setEditingExpenseEntry] = useState<ExpenseEntry | null>(null);
  const [expenseSaving, setExpenseSaving] = useState(false);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [previewRows, setPreviewRows] = useState<WarehouseGoodsItem[]>([]);
  const [replaceAllBeforeImport, setReplaceAllBeforeImport] = useState(false);

  const [goodsEditModalOpen, setGoodsEditModalOpen] = useState(false);
  const [editingGoods, setEditingGoods] = useState<WarehouseGoodsItem | null>(null);

  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<WarehouseEntry | null>(null);
  const [partnerExportDetailOpen, setPartnerExportDetailOpen] = useState(false);
  const [storeExportDetailOpen, setStoreExportDetailOpen] = useState(false);
  const [selectedStockRow, setSelectedStockRow] = useState<StockRow | null>(null);

  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());

  const watchedCoffeeTypeId = Form.useWatch('coffeeTypeId', warehouseForm);
  const watchedQuantity = Form.useWatch('quantity', warehouseForm);
  const watchedRawQuantity = Form.useWatch('rawQuantity', warehouseForm);
  const watchedUnitPrice = Form.useWatch('unitPrice', warehouseForm);
  const watchedExpenseCatalogId = Form.useWatch('expenseCatalogId', expenseEntryForm);

  const goodsOptions = useMemo(
    () =>
      goods.map((g) => ({
        value: g.id || '',
        label: `${g.name}${g.productCode ? ` (${g.productCode})` : ''}`,
        name: g.name,
        baseUnit: (g.baseUnit || 'g').trim(),
        isMass: isMassBaseUnit(g.baseUnit || 'g'),
      })),
    [goods],
  );

  const selectedGoodsOption = useMemo(
    () => goodsOptions.find((o) => o.value === watchedCoffeeTypeId),
    [goodsOptions, watchedCoffeeTypeId],
  );

  const liveTotal = useMemo(() => {
    const qty = Number(watchedQuantity || 0);
    const price = Number(watchedUnitPrice || 0);
    if (!selectedGoodsOption) return 0;
    if (selectedGoodsOption.isMass) {
      const qtyG = selectedGoodsOption.baseUnit.toLowerCase().startsWith('kg') ? qty * 1000 : qty;
      return (qtyG / 1000) * price;
    }
    return qty * price;
  }, [watchedQuantity, watchedUnitPrice, selectedGoodsOption]);

  const filteredEntries = useMemo(() => {
    const monthStr = selectedMonth.format('YYYY-MM');
    return entries.filter((e) => e.month === monthStr);
  }, [entries, selectedMonth]);

  const monthlySummary = useMemo<MonthSummaryRow[]>(() => {
    const map = new Map<string, MonthSummaryRow>();
    filteredEntries
      .filter((e) => e.entryType === 'import')
      .forEach((e) => {
        const key = `${e.coffeeTypeId}__${e.unit}`;
        const ex = map.get(key);
        if (ex) {
          ex.totalQuantity += e.quantity;
          ex.totalAmount += e.totalAmount;
          ex.entryCount += 1;
        } else {
          map.set(key, {
            key,
            coffeeTypeName: e.coffeeTypeName,
            unit: e.unit,
            totalQuantity: e.quantity,
            totalAmount: e.totalAmount,
            entryCount: 1,
          });
        }
      });
    return Array.from(map.values()).sort((a, b) =>
      a.coffeeTypeName.localeCompare(b.coffeeTypeName, 'vi'),
    );
  }, [filteredEntries]);

  const totalQuantityThisMonth = filteredEntries
    .filter((e) => e.entryType === 'import')
    .reduce((s, e) => s + e.quantity, 0);
  const totalAmountThisMonth = filteredEntries
    .filter((e) => e.entryType === 'import')
    .reduce((s, e) => s + e.totalAmount, 0);

  const stockRows = useMemo<StockRow[]>(() => buildWarehouseStockRows(entries), [entries]);

  const selectedPartnerExportDetails = useMemo(() => {
    if (!selectedStockRow) return [];
    return entries
      .filter(
        (entry) =>
          entry.entryType === 'export' &&
          normalizeText(entry.coffeeTypeId) === normalizeText(selectedStockRow.coffeeTypeId) &&
          String(entry.unit || '') === String(selectedStockRow.unit || '') &&
          (entry.exportTargetType === 'partner' || !!entry.partnerId),
      )
      .sort(
        (a, b) =>
          (b.createdAt ? new Date(b.createdAt).getTime() : 0) -
          (a.createdAt ? new Date(a.createdAt).getTime() : 0),
      )
      .map((entry) => ({
        key: entry.id || `${entry.partnerId}-${entry.month}-${entry.quantity}`,
        month: entry.month,
        partnerName: entry.partnerName || 'Đối tác chưa đặt tên',
        quantity: Math.abs(Number(entry.quantity || 0)),
        unit: entry.unit || 'g',
        unitPrice: Number(entry.unitPrice || 0),
        totalAmount: Math.abs(Number(entry.totalAmount || 0)),
        notes: entry.notes || '',
      }));
  }, [entries, selectedStockRow]);

  const selectedStoreExportEntries = useMemo(() => {
    if (!selectedStockRow) return [];
    return entries
      .filter(
        (entry) =>
          entry.entryType === 'export' &&
          normalizeText(entry.coffeeTypeId) === normalizeText(selectedStockRow.coffeeTypeId) &&
          String(entry.unit || '') === String(selectedStockRow.unit || '') &&
          !(entry.exportTargetType === 'partner' || !!entry.partnerId),
      )
      .sort(
        (a, b) =>
          (b.createdAt ? new Date(b.createdAt).getTime() : 0) -
          (a.createdAt ? new Date(a.createdAt).getTime() : 0),
      )
      .map((entry) => ({
        key: entry.id || `${entry.storeLocationId}-${entry.month}-${entry.quantity}`,
        month: entry.month,
        storeName: entry.storeName || entry.storeLocationId || 'Cơ sở chưa đặt tên',
        quantity: Math.abs(Number(entry.quantity || 0)),
        unit: entry.unit || 'g',
        unitPrice: Number(entry.unitPrice || 0),
        totalAmount: Math.abs(Number(entry.totalAmount || 0)),
        purchaseItemId: entry.purchaseItemId || '',
        notes: entry.notes || '',
      }));
  }, [entries, selectedStockRow]);

  const selectedStoreExportSummary = useMemo<StoreExportSummaryRow[]>(() => {
    const map = new Map<string, StoreExportSummaryRow>();
    selectedStoreExportEntries.forEach((e: any) => {
      const key = String(e.storeName || '');
      const ex = map.get(key);
      if (ex) {
        ex.totalQuantity += Number(e.quantity || 0);
        ex.entryCount += 1;
      } else {
        map.set(key, {
          key,
          storeLocationId: undefined,
          storeName: key || 'Cơ sở chưa đặt tên',
          totalQuantity: Number(e.quantity || 0),
          entryCount: 1,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  }, [selectedStoreExportEntries]);

  // Danh sách chi phí theo tháng được chọn
  const filteredExpenseEntries = useMemo(() => {
    const monthStr = selectedMonth.format('YYYY-MM');
    return expenseEntries.filter((e) => e.month === monthStr);
  }, [expenseEntries, selectedMonth]);

  // Tổng hợp chi phí theo danh mục (trong tháng)
  const expenseSummary = useMemo(() => {
    const map = new Map<string, { name: string; total: number; count: number }>();
    filteredExpenseEntries.forEach((e) => {
      const key = e.expenseCatalogId;
      const ex = map.get(key);
      if (ex) {
        ex.total += e.unitPrice;
        ex.count += 1;
      } else {
        map.set(key, { name: e.expenseName, total: e.unitPrice, count: 1 });
      }
    });
    return Array.from(map.entries())
      .map(([id, v]) => ({ key: id, ...v }))
      .sort((a, b) => b.total - a.total);
  }, [filteredExpenseEntries]);

  const totalExpenseThisMonth = filteredExpenseEntries.reduce((s, e) => s + e.unitPrice, 0);

  // Options cho select danh mục chi phí
  const expenseCatalogOptions = useMemo(
    () =>
      expenseCatalog.map((c) => ({
        value: c.id || '',
        label: c.name,
        defaultUnitPrice: c.defaultUnitPrice || 0,
      })),
    [expenseCatalog],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [allGoods, allEntries, allExpenseCatalog, allExpenseEntries] = await Promise.all([
        warehouseGoodsCatalogService.getAll(),
        warehouseService.getAllEntries(),
        expenseCatalogService.getAll(),
        expenseEntryService.getAll(),
      ]);
      setGoods(allGoods || []);
      setEntries(allEntries || []);
      setExpenseCatalog(allExpenseCatalog || []);
      setExpenseEntries(allExpenseEntries || []);
    } catch {
      message.error('Không thể tải dữ liệu kho tổng.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, reloadToken]);

  const refreshPreview = useCallback(() => {
    const parsed = parseGoodsCatalogPaste(pasteText);
    const drafts = parsedRowsToDraftItems(parsed);
    setPreviewRows(
      drafts.map((d, i) => ({
        ...d,
        id: `preview-${i}`,
      })) as WarehouseGoodsItem[],
    );
  }, [pasteText]);

  useEffect(() => {
    if (!importModalOpen) return;
    refreshPreview();
  }, [importModalOpen, refreshPreview]);

  const handleExcelUpload = async (file: File) => {
    try {
      const parsed = await parseGoodsCatalogExcel(file);
      const drafts = parsedRowsToDraftItems(parsed);
      setPreviewRows(
        drafts.map((d, i) => ({
          ...d,
          id: `preview-${i}`,
        })) as WarehouseGoodsItem[],
      );
      message.success(`Đã đọc ${drafts.length} dòng từ Excel.`);
    } catch {
      message.error('Không đọc được file Excel.');
    }
    return false;
  };

  const handleConfirmImport = async () => {
    const drafts = previewRows.length
      ? parsedRowsToDraftItems(
          previewRows.map((r, i) => ({
            productCode: r.productCode,
            name: r.name,
            group: r.group,
            baseUnit: r.baseUnit,
            gramsPerBaseUnit: r.gramsPerBaseUnit,
            lineNumber: i + 1,
          })),
        )
      : parsedRowsToDraftItems(parseGoodsCatalogPaste(pasteText));

    const valid = drafts.filter((r) => r.productCode.trim() && r.name.trim() && r.gramsPerBaseUnit > 0);
    if (valid.length === 0) {
      message.warning(
        'Không có dòng hợp lệ — cần mã + tên + quy đổi G > 0 (hoặc dán 3 cột: Tên, Đơn vị, Quy đổi G).',
      );
      return;
    }

    const dupErrors = validateWarehouseGoodsNamesUnique(valid, goods, replaceAllBeforeImport);
    if (dupErrors.length > 0) {
      message.error(
        dupErrors.length <= 3
          ? dupErrors.join(' ')
          : `${dupErrors.slice(0, 3).join(' ')} … (+${dupErrors.length - 3} lỗi khác)`,
      );
      return;
    }

    setSaving(true);
    try {
      if (replaceAllBeforeImport) {
        await warehouseGoodsCatalogService.deleteAll();
      }
      const { created, updated } = await warehouseGoodsCatalogService.bulkUpsertByProductCode(valid);
      message.success(
        replaceAllBeforeImport
          ? `Đã thay danh mục: ${valid.length} dòng (tạo ${created}, cập nhật ${updated}).`
          : `Đã nhập danh mục: ${valid.length} dòng (tạo ${created}, cập nhật ${updated}).`,
      );
      setImportModalOpen(false);
      setPasteText('');
      setPreviewRows([]);
      await loadData();
    } catch {
      message.error('Không thể lưu danh mục hàng hóa.');
    } finally {
      setSaving(false);
    }
  };

  const openCreateGoodsModal = () => {
    setEditingGoods(null);
    goodsForm.resetFields();
    goodsForm.setFieldsValue({
      baseUnit: 'g',
      group: '',
      productCode: '',
      name: '',
      gramsPerBaseUnit: 1000,
    });
    setGoodsEditModalOpen(true);
  };

  const openEditGoodsModal = (record: WarehouseGoodsItem) => {
    setEditingGoods(record);
    goodsForm.setFieldsValue({
      productCode: record.productCode,
      name: record.name,
      group: record.group,
      baseUnit: record.baseUnit || 'g',
      gramsPerBaseUnit: record.gramsPerBaseUnit ?? 1000,
    });
    setGoodsEditModalOpen(true);
  };

  const handleSaveGoodsRow = async () => {
    try {
      const v = await goodsForm.validateFields();
      const conflict = await warehouseGoodsCatalogService.findNameConflict(
        v.name.trim(),
        editingGoods?.id,
      );
      if (conflict) {
        message.error(`Tên «${v.name.trim()}» đã tồn tại (mã ${conflict.productCode}).`);
        return;
      }
      setSaving(true);
      await warehouseGoodsCatalogService.upsertItem({
        id: editingGoods?.id,
        productCode: v.productCode.trim(),
        name: v.name.trim(),
        group: (v.group || '').trim(),
        baseUnit: (v.baseUnit || 'g').trim(),
        gramsPerBaseUnit: Number(v.gramsPerBaseUnit),
        sortIndex: editingGoods?.sortIndex ?? goods.length,
      });
      message.success(editingGoods ? 'Đã cập nhật dòng danh mục.' : 'Đã thêm dòng danh mục.');
      setGoodsEditModalOpen(false);
      setEditingGoods(null);
      goodsForm.resetFields();
      await loadData();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error('Không thể lưu.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGoods = async (record: WarehouseGoodsItem) => {
    if (!record.id) return;
    try {
      await warehouseGoodsCatalogService.deleteItem(record.id);
      message.success('Đã xóa dòng danh mục.');
      await loadData();
    } catch {
      message.error('Không thể xóa.');
    }
  };

  const handleDeleteAllGoods = async () => {
    try {
      await warehouseGoodsCatalogService.deleteAll();
      message.success('Đã xóa toàn bộ danh mục hàng hóa.');
      await loadData();
    } catch {
      message.error('Không thể xóa toàn bộ.');
    }
  };

  const handleExportGoodsExcel = () => {
    if (!goods.length) {
      message.warning('Chưa có dữ liệu để xuất Excel.');
      return;
    }

    const rows = goods.map((item, index) => ({
      STT: index + 1,
      'Mã sản phẩm': item.productCode || '',
      'Tên nguyên liệu': item.name || '',
      Nhóm: item.group || '',
      'Đơn vị gốc': item.baseUnit || '',
      'Quy đổi G': Number(item.gramsPerBaseUnit || 0),
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'DanhMucHangHoa');

    const fileName = `danh_muc_hang_hoa_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    message.success('Đã xuất file Excel danh mục hàng hóa.');
  };

  // ─── Expense Catalog handlers ───
  const openCreateExpenseCatalogModal = () => {
    setEditingExpenseCatalog(null);
    expenseCatalogForm.resetFields();
    setExpenseCatalogModalOpen(true);
  };

  const openEditExpenseCatalogModal = (record: ExpenseCatalogItem) => {
    setEditingExpenseCatalog(record);
    expenseCatalogForm.setFieldsValue({
      name: record.name,
      defaultUnitPrice: record.defaultUnitPrice,
      notes: record.notes,
    });
    setExpenseCatalogModalOpen(true);
  };

  const handleSaveExpenseCatalog = async () => {
    try {
      const v = await expenseCatalogForm.validateFields();
      setExpenseSaving(true);
      if (editingExpenseCatalog?.id) {
        await expenseCatalogService.updateItem(editingExpenseCatalog.id, {
          name: v.name,
          defaultUnitPrice: Number(v.defaultUnitPrice || 0),
          notes: v.notes,
        });
        message.success('Đã cập nhật danh mục chi phí.');
      } else {
        await expenseCatalogService.addItem({
          name: v.name,
          defaultUnitPrice: Number(v.defaultUnitPrice || 0),
          notes: v.notes,
          sortIndex: expenseCatalog.length,
        });
        message.success('Đã thêm danh mục chi phí.');
      }
      setExpenseCatalogModalOpen(false);
      setEditingExpenseCatalog(null);
      expenseCatalogForm.resetFields();
      await loadData();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error('Không thể lưu danh mục chi phí.');
    } finally {
      setExpenseSaving(false);
    }
  };

  const handleDeleteExpenseCatalog = async (record: ExpenseCatalogItem) => {
    if (!record.id) return;
    try {
      await expenseCatalogService.deleteItem(record.id);
      message.success('Đã xóa danh mục chi phí.');
      await loadData();
    } catch {
      message.error('Không thể xóa.');
    }
  };

  // ─── Expense Entry handlers ───
  const openCreateExpenseEntryModal = () => {
    setEditingExpenseEntry(null);
    expenseEntryForm.resetFields();
    expenseEntryForm.setFieldsValue({ date: dayjs() });
    setExpenseEntryModalOpen(true);
  };

  const openEditExpenseEntryModal = (record: ExpenseEntry) => {
    setEditingExpenseEntry(record);
    expenseEntryForm.setFieldsValue({
      date: record.date ? dayjs(record.date, 'YYYY-MM-DD') : dayjs(record.month, 'YYYY-MM'),
      expenseCatalogId: record.expenseCatalogId,
      unitPrice: record.unitPrice,
      notes: record.notes,
    });
    setExpenseEntryModalOpen(true);
  };

  const handleExpenseCatalogChange = (catalogId: string) => {
    const opt = expenseCatalogOptions.find((o) => o.value === catalogId);
    if (opt?.defaultUnitPrice) {
      expenseEntryForm.setFieldsValue({ unitPrice: opt.defaultUnitPrice });
    }
  };

  const handleSaveExpenseEntry = async () => {
    try {
      const values = await expenseEntryForm.validateFields();
      const dateStr = values.date.format('YYYY-MM-DD');
      const monthStr = values.date.format('YYYY-MM');
      const catalog = expenseCatalog.find((c) => c.id === values.expenseCatalogId);
      if (!catalog?.id) {
        message.error('Không tìm thấy danh mục chi phí.');
        return;
      }
      setExpenseSaving(true);
      if (editingExpenseEntry?.id) {
        await expenseEntryService.updateEntry(editingExpenseEntry.id, {
          date: dateStr,
          month: monthStr,
          expenseCatalogId: catalog.id,
          expenseName: catalog.name,
          unitPrice: Number(values.unitPrice),
          notes: (values.notes || '').trim(),
        });
        message.success('Đã cập nhật mục chi phí.');
      } else {
        await expenseEntryService.addEntry({
          date: dateStr,
          month: monthStr,
          expenseCatalogId: catalog.id,
          expenseName: catalog.name,
          unitPrice: Number(values.unitPrice),
          notes: (values.notes || '').trim(),
        });
        message.success('Đã thêm mục chi phí.');
      }
      setExpenseEntryModalOpen(false);
      setEditingExpenseEntry(null);
      expenseEntryForm.resetFields();
      await loadData();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error('Không thể lưu mục chi phí.');
    } finally {
      setExpenseSaving(false);
    }
  };

  const handleDeleteExpenseEntry = async (id?: string) => {
    if (!id) return;
    try {
      await expenseEntryService.deleteEntry(id);
      message.success('Đã xóa mục chi phí.');
      await loadData();
    } catch {
      message.error('Không thể xóa mục chi phí.');
    }
  };

  const openCreateEntryModal = () => {
    setEditingEntry(null);
    warehouseForm.resetFields();
    warehouseForm.setFieldsValue({ month: selectedMonth, quantity: 0, rawQuantity: 0, unitPrice: 0 });
    setEntryModalOpen(true);
  };

  const openEditEntryModal = (record: WarehouseEntry) => {
    setEditingEntry(record);
    const g = goods.find((x) => x.id === record.coffeeTypeId);
    const isMass = isMassBaseUnit(record.unit || g?.baseUnit || 'g');
    let qty = record.quantity;
    let rawQty = record.rawQuantity;
    let price = record.unitPrice;
    if (isMass && record.unit === 'g' && g?.baseUnit && g.baseUnit.toLowerCase().startsWith('kg')) {
      qty = record.quantity / 1000;
      rawQty = record.rawQuantity === undefined ? undefined : record.rawQuantity / 1000;
      price = record.unitPrice;
    }
    warehouseForm.setFieldsValue({
      month: dayjs(record.month, 'YYYY-MM'),
      coffeeTypeId: record.coffeeTypeId,
      quantity: qty,
      rawQuantity: rawQty,
      unitPrice: price,
      notes: record.notes,
    });
    setEntryModalOpen(true);
  };

  const handleGoodsTypeChange = () => {
    const opt = goodsOptions.find((o) => o.value === warehouseForm.getFieldValue('coffeeTypeId'));
    if (opt?.isMass) {
      warehouseForm.setFieldsValue({ quantity: 0, unitPrice: 0 });
    }
  };

  const handleSaveEntry = async () => {
    try {
      const values = await warehouseForm.validateFields();
      const monthStr = values.month.format('YYYY-MM');
      const selected = goods.find((c) => c.id === values.coffeeTypeId);
      if (!selected?.id) {
        message.error('Không tìm thấy hàng trong danh mục.');
        return;
      }

      const mass = isMassBaseUnit(selected.baseUnit || 'g');
      let quantity = Number(values.quantity);
      let rawQuantity =
        values.rawQuantity === undefined || values.rawQuantity === null || values.rawQuantity === ('' as any)
          ? undefined
          : Number(values.rawQuantity);
      let unit = (selected.baseUnit || 'g').trim();
      let unitPrice = Number(values.unitPrice);
      let amountMode = mass ? ('per1000g' as const) : ('baseUnit' as const);

      if (mass) {
        unit = 'g';
        const uk = (selected.baseUnit || 'g').trim().toLowerCase();
        if (uk === 'kg' || uk === 'kilo' || uk === 'kilogram') {
          quantity = quantity * 1000;
          rawQuantity = rawQuantity === undefined ? undefined : rawQuantity * 1000;
        }
      }

      const payload = {
        month: monthStr,
        coffeeTypeId: selected.id,
        coffeeTypeName: selected.name.trim(),
        unit,
        quantity,
        rawQuantity: rawQuantity !== undefined && Number.isFinite(rawQuantity) && rawQuantity > 0 ? rawQuantity : undefined,
        unitPrice,
        amountMode,
        entryType: 'import' as const,
        notes: (values.notes || '').trim(),
      };

      setSaving(true);
      if (editingEntry?.id) {
        await warehouseService.updateEntry(editingEntry.id, payload);
        message.success('Đã cập nhật mục nhập kho.');
      } else {
        await warehouseService.addEntry(payload);
        message.success('Đã thêm mục nhập kho.');
      }
      setEntryModalOpen(false);
      setEditingEntry(null);
      warehouseForm.resetFields();
      await loadData();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error('Không thể lưu mục nhập kho.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (id?: string) => {
    if (!id) return;
    try {
      await warehouseService.deleteEntry(id);
      message.success('Đã xóa mục nhập kho.');
      await loadData();
    } catch {
      message.error('Không thể xóa mục nhập kho.');
    }
  };

  const catalogColumns = [
    {
      title: 'Mã sản phẩm',
      dataIndex: 'productCode',
      key: 'productCode',
      width: 120,
      render: (v: string) => v || '-',
    },
    {
      title: 'Tên nguyên liệu',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      sorter: (a: WarehouseGoodsItem, b: WarehouseGoodsItem) =>
        a.name.localeCompare(b.name, 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Nhóm',
      dataIndex: 'group',
      key: 'group',
      width: 140,
      render: (v: string) => v || '-',
    },
    {
      title: 'Đơn vị gốc',
      dataIndex: 'baseUnit',
      key: 'baseUnit',
      width: 110,
    },
    {
      title: 'Quy đổi G',
      dataIndex: 'gramsPerBaseUnit',
      key: 'gramsPerBaseUnit',
      width: 100,
      align: 'right' as const,
      render: (v: number) => formatVi(v, 0),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 100,
      render: (_: unknown, record: WarehouseGoodsItem) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditGoodsModal(record)} />
          <Popconfirm
            title="Xóa dòng danh mục?"
            onConfirm={() => handleDeleteGoods(record)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const summaryColumns = [
    { title: 'Hàng hóa', dataIndex: 'coffeeTypeName', key: 'coffeeTypeName', width: 200 },
    { title: 'Đơn vị', dataIndex: 'unit', key: 'unit', width: 80 },
    {
      title: 'Tổng số lượng',
      dataIndex: 'totalQuantity',
      key: 'totalQuantity',
      width: 140,
      align: 'right' as const,
      render: (v: number) => formatVi(v, 2),
      sorter: (a: MonthSummaryRow, b: MonthSummaryRow) => a.totalQuantity - b.totalQuantity,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Tổng thành tiền',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 160,
      align: 'right' as const,
      render: (v: number) => <b>{formatVi(v)}</b>,
      sorter: (a: MonthSummaryRow, b: MonthSummaryRow) => a.totalAmount - b.totalAmount,
      defaultSortOrder: 'descend' as const,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    { title: 'Số lần nhập', dataIndex: 'entryCount', key: 'entryCount', width: 110, align: 'right' as const },
  ];

  const entryColumns = [
    {
      title: 'Tên hàng',
      dataIndex: 'coffeeTypeName',
      key: 'coffeeTypeName',
      width: 200,
      sorter: (a: WarehouseEntry, b: WarehouseEntry) =>
        (a.coffeeTypeName || '').localeCompare(b.coffeeTypeName || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Đã rang',
      key: 'quantity',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, r: WarehouseEntry) => `${formatVi(r.quantity, 2)} ${r.unit}`,
    },
    {
      title: 'Nhân xanh',
      key: 'rawQuantity',
      width: 130,
      align: 'right' as const,
      render: (_: unknown, r: WarehouseEntry) =>
        r.rawQuantity === undefined || r.rawQuantity === null ? '-' : `${formatVi(r.rawQuantity, 2)} ${r.unit}`,
    },
    {
      title: 'Hao hụt',
      key: 'shrinkage',
      width: 140,
      align: 'right' as const,
      render: (_: unknown, r: WarehouseEntry) => {
        const raw = Number(r.rawQuantity);
        const roasted = Number(r.quantity);
        if (!Number.isFinite(raw) || raw <= 0) return '-';
        const shrink = raw - roasted;
        const pct = (shrink / raw) * 100;
        const tagColor = shrink < 0 ? 'volcano' : shrink === 0 ? 'default' : 'green';
        return (
          <Space size={6}>
            <Tag color={tagColor} style={{ marginRight: 0 }}>
              {formatVi(shrink, 2)} {r.unit}
            </Tag>
            <Text type="secondary">({formatVi(pct, 1)}%)</Text>
          </Space>
        );
      },
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 130,
      align: 'right' as const,
      render: (v: number) => formatVi(v),
    },
    {
      title: 'Thành tiền',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 150,
      align: 'right' as const,
      render: (v: number) => <b>{formatVi(v)}</b>,
      sorter: (a: WarehouseEntry, b: WarehouseEntry) => a.totalAmount - b.totalAmount,
      defaultSortOrder: 'descend' as const,
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    { title: 'Ghi chú', dataIndex: 'notes', key: 'notes', width: 180, render: (v?: string) => v || '-' },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 100,
      render: (_: unknown, record: WarehouseEntry) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditEntryModal(record)} />
          <Popconfirm
            title="Xóa mục nhập kho?"
            onConfirm={() => handleDeleteEntry(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const roastedQuantityLabel = selectedGoodsOption?.isMass
    ? selectedGoodsOption.baseUnit.toLowerCase().startsWith('kg')
      ? 'Số lượng đã rang (kg)'
      : 'Số lượng đã rang (g)'
    : `Số lượng đã rang (${selectedGoodsOption?.baseUnit || 'đơn vị'})`;

  const rawQuantityLabel = selectedGoodsOption?.isMass
    ? selectedGoodsOption.baseUnit.toLowerCase().startsWith('kg')
      ? 'Số lượng nhân xanh (kg)'
      : 'Số lượng nhân xanh (g)'
    : `Số lượng nhân xanh (${selectedGoodsOption?.baseUnit || 'đơn vị'})`;

  const priceLabel = selectedGoodsOption?.isMass ? 'Đơn giá (đ/kg)' : 'Đơn giá / 1 đơn vị';

  const expenseSelectedCatalogOpt = expenseCatalogOptions.find((o) => o.value === watchedExpenseCatalogId);

  return (
    <>
      <Collapse
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'catalog',
            label: (
              <span style={{ fontWeight: 600 }}>
                Danh mục hàng hóa
                {goods.length > 0 && <Tag style={{ marginLeft: 8 }}>{goods.length} mặt hàng</Tag>}
              </span>
            ),
            extra: (
              <Space onClick={(e) => e.stopPropagation()}>
                {goods.length > 0 && (
                  <Popconfirm
                    title="Xóa toàn bộ danh mục?"
                    description={`Sẽ xóa ${goods.length} dòng. Không thể hoàn tác.`}
                    onConfirm={handleDeleteAllGoods}
                    okText="Xóa hết"
                    okButtonProps={{ danger: true }}
                    cancelText="Hủy"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />}>
                      Xóa tất cả
                    </Button>
                  </Popconfirm>
                )}
                <Button size="small" onClick={() => setImportModalOpen(true)}>
                  Dán / Excel
                </Button>
                <Button size="small" icon={<DownloadOutlined />} onClick={handleExportGoodsExcel}>
                  Xuất Excel
                </Button>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreateGoodsModal}>
                  Thêm một dòng
                </Button>
              </Space>
            ),
            children: (
              <Table
                rowKey={(r) => r.id || r.productCode || r.name}
                columns={catalogColumns}
                dataSource={goods}
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 1020 }}
                locale={{ emptyText: 'Chưa có danh mục — dùng «Dán / Excel» hoặc «Thêm một dòng».' }}
              />
            ),
          },
          {
            key: 'expense-catalog',
            label: (
              <span style={{ fontWeight: 600 }}>
                <DollarOutlined style={{ marginRight: 6 }} />
                Danh mục chi phí
                {expenseCatalog.length > 0 && (
                  <Tag style={{ marginLeft: 8 }}>
                    {expenseCatalog.length} loại chi phí
                  </Tag>
                )}
              </span>
            ),
            extra: (
              <Space onClick={(e) => e.stopPropagation()}>
                <Button
                  size="small"
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openCreateExpenseCatalogModal}
                >
                  Thêm chi phí
                </Button>
              </Space>
            ),
            children: (
              <Table
                rowKey={(r) => r.id || r.name}
                columns={[
                  {
                    title: 'Tên chi phí',
                    dataIndex: 'name',
                    key: 'name',
                    width: 220,
                    sorter: (a: ExpenseCatalogItem, b: ExpenseCatalogItem) =>
                      a.name.localeCompare(b.name, 'vi'),
                    sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
                  },
                  {
                    title: 'Đơn giá mặc định (đ)',
                    dataIndex: 'defaultUnitPrice',
                    key: 'defaultUnitPrice',
                    width: 180,
                    align: 'right' as const,
                    render: (v: number) => (v ? <b>{formatVi(v)}</b> : <Text type="secondary">—</Text>),
                  },
                  {
                    title: 'Ghi chú',
                    dataIndex: 'notes',
                    key: 'notes',
                    render: (v: string) => v || '-',
                  },
                  {
                    title: 'Thao tác',
                    key: 'actions',
                    fixed: 'right' as const,
                    width: 100,
                    render: (_: unknown, record: ExpenseCatalogItem) => (
                      <Space>
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => openEditExpenseCatalogModal(record)}
                        />
                        <Popconfirm
                          title="Xóa danh mục chi phí?"
                          onConfirm={() => handleDeleteExpenseCatalog(record)}
                          okText="Xóa"
                          cancelText="Hủy"
                        >
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
                dataSource={expenseCatalog}
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 600 }}
                locale={{ emptyText: 'Chưa có danh mục chi phí — nhấn «Thêm chi phí» để thêm.' }}
              />
            ),
          },
        ]}
      />

      <Modal
        title="Nhập danh mục hàng hóa (dán hoặc Excel)"
        open={importModalOpen}
        onCancel={() => {
          setImportModalOpen(false);
          setPasteText('');
          setPreviewRows([]);
        }}
        onOk={handleConfirmImport}
        confirmLoading={saving}
        okText="Lưu vào danh mục"
        width={720}
        destroyOnClose
      >
        <p style={{ marginBottom: 8, color: '#595959' }}>
          <b>3 cột</b> (như bảng mẫu): <b>Tên hàng</b>, tab, <b>Đơn vị</b>, tab, <b>Quy đổi G</b> (gram trên 1 đơn vị) — hệ
          thống tự sinh mã.
          <br />
          Hoặc <b>5 cột</b>: Mã, Tên, Nhóm, Đơn vị, Quy đổi G. <b>4 cột</b>: Mã, Tên, Nhóm, Đơn vị (quy đổi suy ra từ đơn
          vị) hoặc Mã, Tên, Đơn vị, Quy đổi G.
          <br />
          <span style={{ color: '#cf1322' }}>Không cho hai dòng trùng tên khác mã</span> (trong file và với danh mục hiện
          tại khi không chọn «xóa hết trước khi nhập»).
        </p>
        <Upload beforeUpload={handleExcelUpload} showUploadList={false} accept=".xlsx,.xls">
          <Button icon={<UploadOutlined />}>Chọn file Excel</Button>
        </Upload>
        <div style={{ marginTop: 12 }}>
          <Input.TextArea
            rows={6}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Dán từ Excel / Google Sheets..."
          />
          <Button type="link" size="small" onClick={refreshPreview} style={{ paddingLeft: 0 }}>
            Làm mới xem trước từ ô dán
          </Button>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <input
            type="checkbox"
            checked={replaceAllBeforeImport}
            onChange={(e) => setReplaceAllBeforeImport(e.target.checked)}
          />
          Xóa toàn bộ danh mục hiện tại trước khi nhập
        </label>
        <Table
          style={{ marginTop: 12 }}
          size="small"
          pagination={false}
          scroll={{ y: 220 }}
          rowKey={(r) => r.id || r.productCode}
          dataSource={previewRows}
          columns={[
            { title: 'Mã', dataIndex: 'productCode', width: 90 },
            { title: 'Tên', dataIndex: 'name' },
            { title: 'Nhóm', dataIndex: 'group', width: 88 },
            { title: 'ĐVT', dataIndex: 'baseUnit', width: 64 },
            {
              title: 'Quy đổi G',
              dataIndex: 'gramsPerBaseUnit',
              width: 88,
              align: 'right' as const,
              render: (v: number) => (v > 0 ? formatVi(v, 0) : '—'),
            },
          ]}
          locale={{ emptyText: 'Chưa có xem trước — dán dữ liệu hoặc chọn Excel.' }}
        />
      </Modal>

      <Modal
        title={editingGoods ? 'Sửa dòng danh mục' : 'Thêm dòng danh mục'}
        open={goodsEditModalOpen}
        onCancel={() => {
          setGoodsEditModalOpen(false);
          setEditingGoods(null);
          goodsForm.resetFields();
        }}
        onOk={handleSaveGoodsRow}
        confirmLoading={saving}
        okText="Lưu"
        destroyOnClose
      >
        <Form form={goodsForm} layout="vertical">
          <Form.Item
            name="productCode"
            label="Mã sản phẩm"
            rules={[{ required: true, message: 'Nhập mã sản phẩm' }]}
          >
            <Input placeholder="VD: NL-CF-01" disabled={!!editingGoods?.id} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Tên nguyên liệu"
            rules={[{ required: true, message: 'Nhập tên' }]}
          >
            <Input placeholder="Tên hiển thị khi chọn từ kho tổng" />
          </Form.Item>
          <Form.Item name="group" label="Nhóm">
            <Input placeholder="VD: Hạt cà phê, Sữa, Syrup..." />
          </Form.Item>
          <Form.Item
            name="baseUnit"
            label="Đơn vị gốc"
            rules={[{ required: true, message: 'Nhập đơn vị' }]}
          >
            <Input placeholder="g, kg, chai, ml..." />
          </Form.Item>
          <Form.Item
            name="gramsPerBaseUnit"
            label="Quy đổi G (g / 1 đơn vị gốc)"
            rules={[
              { required: true, message: 'Nhập quy đổi' },
              {
                validator: (_, v) =>
                  Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error('Phải > 0')),
              },
            ]}
          >
            <InputNumber min={0.0001} style={{ width: '100%' }} placeholder="VD: 1000 (1 kg), 1284 (1 hộp sữa đặc)" />
          </Form.Item>
        </Form>
      </Modal>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <DatePicker
              picker="month"
              style={{ width: '100%' }}
              value={selectedMonth}
              onChange={(v) => setSelectedMonth(v || dayjs())}
              format="MM/YYYY"
              placeholder="Chọn tháng"
            />
          </Col>
          <Col xs={24} md={16} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEntryModal}>
              Thêm nhập kho
            </Button>
          </Col>
        </Row>

        <Row gutter={[16, 12]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <Statistic title="Tháng" value={selectedMonth.format('MM/YYYY')} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic title="Số mục nhập kho" value={filteredEntries.filter((e) => e.entryType === 'import').length} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Tổng SL (nhập tháng)"
              value={totalQuantityThisMonth}
              formatter={(v) => formatVi(Number(v), 2)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Tổng giá trị kho"
              value={totalAmountThisMonth}
              formatter={(v) => formatVi(Number(v))}
            />
          </Col>
        </Row>

        {(filteredExpenseEntries.length > 0 || expenseCatalog.length > 0) && (
          <>
            <Divider style={{ margin: '16px 0 12px' }} />
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Chi phí tháng này"
                  value={totalExpenseThisMonth}
                  formatter={(v) => (
                    <b>{formatVi(Number(v))} đ</b>
                  )}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="Số mục chi phí"
                  value={filteredExpenseEntries.length}
                />
              </Col>
            </Row>
          </>
        )}
      </Card>

      <Card title={`Tổng hợp kho tổng tháng ${selectedMonth.format('MM/YYYY')}`} style={{ marginBottom: 16 }}>
        <Table
          rowKey="key"
          columns={summaryColumns}
          dataSource={monthlySummary}
          loading={loading}
          scroll={{ x: 700 }}
          pagination={false}
          locale={{ emptyText: 'Chưa có dữ liệu kho tổng trong tháng này.' }}
        />
      </Card>

      {/* Bảng chi phí tháng */}
      <Card
        title={
          <span>
            <DollarOutlined style={{ marginRight: 6 }} />
            Chi phí tháng {selectedMonth.format('MM/YYYY')}
          </span>
        }
        extra={
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateExpenseEntryModal}
          >
            Nhập chi phí
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Table
          rowKey={(r) => r.id || `${r.expenseName}-${r.month}`}
          columns={[
            {
              title: 'Ngày',
              dataIndex: 'date',
              key: 'date',
              width: 110,
              render: (v: string) => v ? dayjs(v, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-',
              sorter: (a: ExpenseEntry, b: ExpenseEntry) => (a.date || a.month).localeCompare(b.date || b.month),
              defaultSortOrder: 'descend' as const,
              sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
            },
            {
              title: 'Tên chi phí',
              dataIndex: 'expenseName',
              key: 'expenseName',
              width: 220,
            },
            {
              title: 'Số tiền (đ)',
              dataIndex: 'unitPrice',
              key: 'unitPrice',
              width: 160,
              align: 'right' as const,
              render: (v: number) => <b>{formatVi(v)}</b>,
              sorter: (a: ExpenseEntry, b: ExpenseEntry) => a.unitPrice - b.unitPrice,
              defaultSortOrder: 'descend' as const,
              sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
            },
            {
              title: 'Ghi chú',
              dataIndex: 'notes',
              key: 'notes',
              render: (v?: string) => v || '-',
            },
            {
              title: 'Thao tác',
              key: 'actions',
              fixed: 'right' as const,
              width: 100,
              render: (_: unknown, record: ExpenseEntry) => (
                <Space>
                  <Button
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => openEditExpenseEntryModal(record)}
                  />
                  <Popconfirm
                    title="Xóa mục chi phí?"
                    onConfirm={() => handleDeleteExpenseEntry(record.id)}
                    okText="Xóa"
                    cancelText="Hủy"
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
          dataSource={filteredExpenseEntries}
          loading={loading}
          scroll={{ x: 600 }}
          pagination={{
            pageSize: 10,
            pageSizeOptions: [10, 20, 50],
            showSizeChanger: true,
            locale: { items_per_page: 'dòng/trang' },
          }}
          locale={{ emptyText: 'Chưa có chi phí nào trong tháng này — nhấn «Nhập chi phí».' }}
          summary={() => (
            filteredExpenseEntries.length > 0 ? (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <b>Tổng cộng</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <b style={{ fontSize: 15 }}>{formatVi(totalExpenseThisMonth)} đ</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3} />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          )}
        />
      </Card>

      <Card title="Chi tiết nhập kho tổng">
        <Table
          rowKey={(r) => r.id || `${r.coffeeTypeName}-${r.month}`}
          columns={entryColumns}
          dataSource={filteredEntries.filter((e) => e.entryType === 'import')}
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 10,
            pageSizeOptions: [10, 20, 50],
            showSizeChanger: true,
            locale: { items_per_page: 'dòng/trang' },
          }}
          locale={{ emptyText: 'Chưa có mục nhập kho nào trong tháng này.' }}
        />
      </Card>

      <Card title="Tồn kho tổng (theo mặt hàng + đơn vị)" style={{ marginTop: 16 }}>
        <Table
          rowKey="key"
          columns={[
            {
              title: 'Hàng hóa',
              dataIndex: 'coffeeTypeName',
              key: 'coffeeTypeName',
              width: 220,
              render: (value: string, row: StockRow) => (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => {
                    setSelectedStockRow(row);
                    setPartnerExportDetailOpen(true);
                  }}
                >
                  {value}
                </Button>
              ),
            },
            { title: 'Đơn vị', dataIndex: 'unit', key: 'unit', width: 80 },
            {
              title: 'Tổng nhập',
              dataIndex: 'totalImport',
              key: 'totalImport',
              width: 120,
              align: 'right' as const,
              render: (v: number) => formatVi(v, 2),
            },
            {
              title: 'Xuất cơ sở',
              dataIndex: 'totalExportStore',
              key: 'totalExportStore',
              width: 120,
              align: 'right' as const,
              render: (v: number, row: StockRow) => (
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => {
                    setSelectedStockRow(row);
                    setStoreExportDetailOpen(true);
                  }}
                >
                  {formatVi(v, 2)}
                </Button>
              ),
            },
            {
              title: 'Xuất đối tác',
              dataIndex: 'totalExportPartner',
              key: 'totalExportPartner',
              width: 120,
              align: 'right' as const,
              render: (v: number) => formatVi(v, 2),
            },
            {
              title: 'Còn tồn',
              dataIndex: 'stock',
              key: 'stock',
              width: 120,
              align: 'right' as const,
              render: (v: number) => (
                <b style={{ color: v < 0 ? '#cf1322' : v === 0 ? '#8c8c8c' : '#389e0d' }}>
                  {formatVi(v, 2)}
                </b>
              ),
            },
          ]}
          dataSource={stockRows}
          loading={loading}
          scroll={{ x: 760 }}
          pagination={false}
          locale={{ emptyText: 'Chưa có dữ liệu tồn kho.' }}
        />
      </Card>

      <Modal
        title={`Chi tiết xuất cho đối tác${selectedStockRow ? ` - ${selectedStockRow.coffeeTypeName}` : ''}`}
        open={partnerExportDetailOpen}
        onCancel={() => {
          setPartnerExportDetailOpen(false);
          setSelectedStockRow(null);
        }}
        footer={null}
        width={900}
      >
        <Table
          rowKey="key"
          dataSource={selectedPartnerExportDetails}
          pagination={{ pageSize: 8 }}
          scroll={{ x: 780 }}
          locale={{ emptyText: 'Chưa có lượt xuất cho đối tác.' }}
          columns={[
            { title: 'Tháng', dataIndex: 'month', key: 'month', width: 110 },
            { title: 'Đối tác', dataIndex: 'partnerName', key: 'partnerName', width: 220 },
            {
              title: 'SL',
              dataIndex: 'quantity',
              key: 'quantity',
              width: 100,
              align: 'right' as const,
              render: (value: number, row: { unit: string }) => `${formatVi(value, 2)} ${row.unit}`,
            },
            {
              title: 'Đơn giá',
              dataIndex: 'unitPrice',
              key: 'unitPrice',
              width: 120,
              align: 'right' as const,
              render: (value: number) => formatVi(value, 0),
            },
            {
              title: 'Thành tiền',
              dataIndex: 'totalAmount',
              key: 'totalAmount',
              width: 140,
              align: 'right' as const,
              render: (value: number) => formatVi(value, 0),
            },
            { title: 'Ghi chú', dataIndex: 'notes', key: 'notes', render: (v: string) => v || '-' },
          ]}
        />
      </Modal>

      <Modal
        title={`Chi tiết xuất cho cơ sở${selectedStockRow ? ` - ${selectedStockRow.coffeeTypeName}` : ''}`}
        open={storeExportDetailOpen}
        onCancel={() => {
          setStoreExportDetailOpen(false);
          setSelectedStockRow(null);
        }}
        footer={null}
        width={980}
      >
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Statistic title="Tổng xuất cho cơ sở" value={selectedStockRow?.totalExportStore || 0} formatter={(v) => formatVi(Number(v), 2)} />
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Tổng nhập" value={selectedStockRow?.totalImport || 0} formatter={(v) => formatVi(Number(v), 2)} />
            </Col>
            <Col xs={24} md={8}>
              <Statistic title="Còn tồn" value={selectedStockRow?.stock || 0} formatter={(v) => formatVi(Number(v), 2)} />
            </Col>
          </Row>
        </Card>

        <Card size="small" title="Tổng hợp theo cơ sở" style={{ marginBottom: 12 }}>
          <Table
            rowKey="key"
            dataSource={selectedStoreExportSummary}
            pagination={false}
            locale={{ emptyText: 'Chưa có lượt xuất cho cơ sở.' }}
            columns={[
              { title: 'Cơ sở', dataIndex: 'storeName', key: 'storeName' },
              {
                title: 'SL xuất',
                dataIndex: 'totalQuantity',
                key: 'totalQuantity',
                width: 140,
                align: 'right' as const,
                render: (v: number) => `${formatVi(v, 2)} ${selectedStockRow?.unit || ''}`,
              },
              {
                title: 'Số lần',
                dataIndex: 'entryCount',
                key: 'entryCount',
                width: 110,
                align: 'right' as const,
              },
            ]}
          />
        </Card>

        <Card size="small" title="Chi tiết các lượt cơ sở lấy hàng">
          <Table
            rowKey="key"
            dataSource={selectedStoreExportEntries as any}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 900 }}
            locale={{ emptyText: 'Chưa có lượt xuất cho cơ sở.' }}
            columns={[
              { title: 'Tháng', dataIndex: 'month', key: 'month', width: 110 },
              { title: 'Cơ sở', dataIndex: 'storeName', key: 'storeName', width: 220 },
              {
                title: 'SL',
                dataIndex: 'quantity',
                key: 'quantity',
                width: 120,
                align: 'right' as const,
                render: (value: number, row: any) => `${formatVi(value, 2)} ${row.unit}`,
              },
              {
                title: 'Đơn giá',
                dataIndex: 'unitPrice',
                key: 'unitPrice',
                width: 120,
                align: 'right' as const,
                render: (value: number) => formatVi(value, 0),
              },
              {
                title: 'Thành tiền',
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                width: 140,
                align: 'right' as const,
                render: (value: number) => formatVi(value, 0),
              },
              { title: 'Mã phiếu NL', dataIndex: 'purchaseItemId', key: 'purchaseItemId', width: 140, render: (v: string) => v || '-' },
              { title: 'Ghi chú', dataIndex: 'notes', key: 'notes', render: (v: string) => v || '-' },
            ]}
          />
        </Card>
      </Modal>

      {/* Modal thêm/sửa danh mục chi phí */}
      <Modal
        title={editingExpenseCatalog ? 'Sửa danh mục chi phí' : 'Thêm danh mục chi phí'}
        open={expenseCatalogModalOpen}
        onCancel={() => {
          setExpenseCatalogModalOpen(false);
          setEditingExpenseCatalog(null);
          expenseCatalogForm.resetFields();
        }}
        onOk={handleSaveExpenseCatalog}
        confirmLoading={expenseSaving}
        okText="Lưu"
        width={480}
        destroyOnClose
      >
        <Form form={expenseCatalogForm} layout="vertical">
          <Form.Item
            name="name"
            label="Tên chi phí"
            rules={[{ required: true, message: 'Nhập tên chi phí' }]}
          >
            <Input placeholder="VD: Phí ship, Hoa tươi, Chổi..." />
          </Form.Item>
          <Form.Item name="defaultUnitPrice" label="Đơn giá mặc định (đ)">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              placeholder="Không bắt buộc — sẽ điền sẵn khi nhập chi phí"
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </Form.Item>
          <Form.Item name="notes" label="Ghi chú">
            <Input placeholder="Ghi chú (không bắt buộc)" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal nhập chi phí hàng ngày */}
      <Modal
        title={editingExpenseEntry ? 'Sửa mục chi phí' : 'Nhập chi phí hàng ngày'}
        open={expenseEntryModalOpen}
        onCancel={() => {
          setExpenseEntryModalOpen(false);
          setEditingExpenseEntry(null);
          expenseEntryForm.resetFields();
        }}
        onOk={handleSaveExpenseEntry}
        confirmLoading={expenseSaving}
        okText="Lưu"
        width={520}
        destroyOnClose
      >
        <Form form={expenseEntryForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="date"
                label="Ngày"
                rules={[{ required: true, message: 'Chọn ngày' }]}
              >
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="expenseCatalogId"
                label="Loại chi phí"
                rules={[{ required: true, message: 'Chọn loại chi phí' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn từ danh mục chi phí..."
                  options={expenseCatalogOptions}
                  onChange={handleExpenseCatalogChange}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: '4px 0' }} />
                      <div style={{ padding: '4px 8px' }}>
                        <Button
                          type="link"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={() => {
                            setExpenseEntryModalOpen(false);
                            openCreateExpenseCatalogModal();
                          }}
                        >
                          Thêm loại chi phí mới
                        </Button>
                      </div>
                    </>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          {expenseSelectedCatalogOpt && (
            <div
              style={{
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 6,
                padding: '8px 12px',
                marginBottom: 12,
                fontSize: 13,
                color: '#873800',
              }}
            >
              <DollarOutlined style={{ marginRight: 4 }} />
              <b>{expenseSelectedCatalogOpt.label}</b>
              {expenseSelectedCatalogOpt.defaultUnitPrice > 0 && (
                <> — Giá mặc định: <b>{formatVi(expenseSelectedCatalogOpt.defaultUnitPrice)} đ</b></>
              )}
            </div>
          )}

          <Form.Item
            name="unitPrice"
            label="Số tiền (đ)"
            rules={[
              { required: true, message: 'Nhập số tiền' },
              {
                validator: (_, v) =>
                  Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error('Phải > 0')),
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              placeholder="Nhập số tiền chi phí (đ)"
            />
          </Form.Item>

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={2} placeholder="Ghi chú (không bắt buộc)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingEntry ? 'Sửa mục nhập kho tổng' : 'Thêm mục nhập kho tổng'}
        open={entryModalOpen}
        onCancel={() => {
          setEntryModalOpen(false);
          setEditingEntry(null);
          warehouseForm.resetFields();
        }}
        onOk={handleSaveEntry}
        confirmLoading={saving}
        okText="Lưu"
        width={680}
        destroyOnClose
      >
        <Form form={warehouseForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="month" label="Tháng nhập kho" rules={[{ required: true, message: 'Chọn tháng' }]}>
                <DatePicker picker="month" style={{ width: '100%' }} format="MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="coffeeTypeId"
                label="Hàng trong danh mục"
                rules={[{ required: true, message: 'Chọn hàng' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Chọn từ danh mục hàng hóa..."
                  options={goodsOptions}
                  onChange={handleGoodsTypeChange}
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedGoodsOption && (
            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
                padding: '8px 12px',
                marginBottom: 12,
                fontSize: 13,
                color: '#389e0d',
              }}
            >
              <b>Đơn vị gốc:</b> {selectedGoodsOption.baseUnit}
              {selectedGoodsOption.isMass && ' — nhập theo khối lượng (g hoặc kg), đơn giá đ/kg'}
            </div>
          )}

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="quantity"
                label={roastedQuantityLabel}
                rules={[
                  { required: true, message: 'Nhập số lượng' },
                  {
                    validator: (_, v) =>
                      Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error('Phải > 0')),
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="unitPrice"
                label={priceLabel}
                rules={[
                  { required: true, message: 'Nhập đơn giá' },
                  {
                    validator: (_, v) =>
                      Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error('Phải > 0')),
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                name="rawQuantity"
                label={rawQuantityLabel}
                rules={[
                  { required: true, message: 'Nhập số lượng nhân xanh' },
                  {
                    validator: (_, v) =>
                      Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error('Phải > 0')),
                  },
                  {
                    validator: (_, v) => {
                      const raw = Number(v);
                      const roasted = Number(warehouseForm.getFieldValue('quantity'));
                      if (!Number.isFinite(raw) || raw <= 0 || !Number.isFinite(roasted) || roasted <= 0) {
                        return Promise.resolve();
                      }
                      return raw >= roasted
                        ? Promise.resolve()
                        : Promise.reject(new Error('Nhân xanh phải ≥ đã rang'));
                    },
                  },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={0} placeholder="Bắt buộc" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {(() => {
                const raw = Number(watchedRawQuantity);
                const roasted = Number(watchedQuantity);
                if (!selectedGoodsOption) return null;
                if (!Number.isFinite(raw) || raw <= 0 || !Number.isFinite(roasted) || roasted <= 0) return null;
                const shrink = raw - roasted;
                const pct = (shrink / raw) * 100;
                const tagColor = shrink < 0 ? 'volcano' : shrink === 0 ? 'default' : 'green';
                const unitLabel = selectedGoodsOption.isMass
                  ? selectedGoodsOption.baseUnit.toLowerCase().startsWith('kg')
                    ? 'kg'
                    : 'g'
                  : selectedGoodsOption.baseUnit;
                return (
                  <div
                    style={{
                      background: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: 6,
                      padding: '8px 12px',
                      marginBottom: 12,
                      fontSize: 13,
                      color: '#389e0d',
                    }}
                  >
                    <b>Hao hụt:</b>{' '}
                    <Tag color={tagColor} style={{ marginRight: 6 }}>
                      {formatVi(shrink, 2)} {unitLabel}
                    </Tag>
                    <Text type="secondary">({formatVi(pct, 1)}%)</Text>
                  </div>
                );
              })()}
            </Col>
          </Row>

          {liveTotal > 0 && (
            <div
              style={{
                background: '#fff7e6',
                border: '1px solid #ffd591',
                borderRadius: 6,
                padding: '8px 12px',
                marginBottom: 12,
                fontSize: 13,
              }}
            >
              <b>Thành tiền dự tính:</b>{' '}
              <Tag color="orange" style={{ fontSize: 14 }}>
                {formatVi(liveTotal)} đ
              </Tag>
            </div>
          )}

          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={2} placeholder="Ghi chú (không bắt buộc)" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default KhoTongSection;
